/**
 * Halaman Laporan Pembelian LPG PSO
 * Menampilkan transaksi LPG dengan fitur upload, filter, download, dan delete
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Upload, Download, Trash2, FileText, X, Plus, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Package, Pencil, MoreVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDateIndonesia, formatCurrency, formatDateForInput, getTodayDate } from '@/utils/formatters';

// Remove trailing slash to prevent double slash
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
const API = `${BACKEND_URL}/api`;

const LPGReport = ({ token, currentUser }) => {
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('creation_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Product details modal states
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  
  // Bulk delete states
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Confirmation dialog states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Edit states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Manual entry states
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    transaction_id: '',
    schedule_agreement_number: '',
    creation_date: new Date().toISOString().slice(0, 16),
    net_price: '',
    ppn: '',
    ppbkb: '',
    pph: '',
    total_amount: ''
  });
  
  // Product details states for manual entry
  const [productDetails, setProductDetails] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [sortBy, sortOrder, currentPage, itemsPerPage]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        limit: itemsPerPage
      };
      if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
      if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/transactions`, { params });
      setTransactions(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Gagal mengambil data transaksi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validasi file
    const validFiles = files.filter(file => {
      const isExcel = file.name.endsWith('.xls') || file.name.endsWith('.xlsx');
      if (!isExcel) {
        toast.error(`${file.name} bukan file Excel`);
      }
      return isExcel;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Pilih minimal satu file Excel');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    // Tambahkan semua file ke FormData
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/transactions/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      setSelectedFiles([]); // Clear selected files
      setCurrentPage(1); // Reset to page 1
      fetchTransactions();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal mengupload file';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
      if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');

      const response = await axios.get(`${API}/transactions/download`, {
        params,
        responseType: 'blob'
      });

      // Generate filename
      let filename = 'Pembelian LPG 3 kg';
      if (startDate && endDate) {
        filename += ` ${format(startDate, 'dd-MM-yyyy')} sampai ${format(endDate, 'dd-MM-yyyy')}`;
      } else if (startDate) {
        filename += ` dari ${format(startDate, 'dd-MM-yyyy')}`;
      } else if (endDate) {
        filename += ` sampai ${format(endDate, 'dd-MM-yyyy')}`;
      }
      filename += '.xlsx';

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('File berhasil didownload');
    } catch (error) {
      toast.error('Gagal mendownload file');
      console.error(error);
    }
  };

  // Handle checkbox selection
  const handleSelectRow = (transactionId) => {
    setSelectedRows(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll || selectedRows.length === transactions.length) {
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      setSelectedRows(transactions.map(t => t.transaction_id));
      setSelectAll(true);
    }
  };

  // Reset selection when page or data changes
  useEffect(() => {
    setSelectedRows([]);
    setSelectAll(false);
  }, [currentPage, transactions]);

  // Delete handlers with confirmation dialogs
  const handleDelete = (transactionId) => {
    setItemToDelete(transactionId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/transactions/${itemToDelete}`);
      toast.success('Transaksi berhasil dihapus');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      fetchTransactions();
    } catch (error) {
      toast.error('Gagal menghapus transaksi');
      console.error(error);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      toast.error('Pilih minimal satu transaksi untuk dihapus');
      return;
    }
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await axios.delete(`${API}/transactions/bulk`, {
        data: selectedRows
      });
      toast.success(`Berhasil menghapus ${selectedRows.length} transaksi`);
      setBulkDeleteConfirmOpen(false);
      setSelectedRows([]);
      setSelectAll(false);
      fetchTransactions();
    } catch (error) {
      toast.error('Gagal menghapus transaksi');
      console.error(error);
    }
  };

  const handleDeleteAll = () => {
    setDeleteAllConfirmOpen(true);
  };

  const confirmDeleteAll = async () => {
    try {
      await axios.delete(`${API}/transactions`);
      toast.success('Semua transaksi berhasil dihapus');
      setDeleteAllConfirmOpen(false);
      setSelectedRows([]);
      setSelectAll(false);
      fetchTransactions();
    } catch (error) {
      toast.error('Gagal menghapus transaksi');
      console.error(error);
    }
  };

  const handleFilter = () => {
    setCurrentPage(1); // Reset ke page 1 saat filter
    fetchTransactions();
  };

  const handleResetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1); // Reset ke page 1
    fetchTransactions();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset ke page 1 saat ganti items per page
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleViewProducts = async (transactionId) => {
    setSelectedTransactionId(transactionId);
    setLoadingProducts(true);
    setProductModalOpen(true);
    
    try {
      const response = await axios.get(`${API}/transactions/${transactionId}/products`);
      setSelectedProducts(response.data.products);
    } catch (error) {
      toast.error('Gagal mengambil data produk');
      console.error(error);
      setSelectedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEdit = async (transaction) => {
    setEditingTransaction(transaction);
    
    // Pre-fill form dengan data transaction
    setManualForm({
      transaction_id: transaction.transaction_id,
      schedule_agreement_number: transaction.schedule_agreement_number,
      creation_date: new Date(transaction.creation_date).toISOString().slice(0, 16),
      net_price: transaction.net_price || '',
      ppn: transaction.ppn || '',
      ppbkb: transaction.ppbkb || '',
      pph: transaction.pph || '',
      total_amount: transaction.total_amount || ''
    });
    
    // Load product details
    try {
      const response = await axios.get(`${API}/transactions/${transaction.transaction_id}/products`);
      const products = response.data.products || [];
      
      // Convert product details ke format form
      const formattedProducts = products.map(p => ({
        material: p.material || '',
        trip_number: p.trip_number,
        quantity: p.quantity,
        unit: p.unit,
        delivery_date: p.delivery_date ? new Date(p.delivery_date.split('/').reverse().join('-')).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        transporter: p.transporter || '',
        confirmed_quantity: p.confirmed_quantity || '',
        confirmed_date: p.confirmed_date ? new Date(p.confirmed_date.split('/').reverse().join('-')).toISOString().slice(0, 10) : ''
      }));
      
      setProductDetails(formattedProducts);
      setShowProductForm(formattedProducts.length > 0);
    } catch (error) {
      console.error('Error loading product details:', error);
      setProductDetails([]);
      setShowProductForm(false);
    }
    
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingTransaction) return;
    
    // Validation
    if (!manualForm.schedule_agreement_number) {
      toast.error('Schedule Agreement Number wajib diisi');
      return;
    }

    // Calculate total
    const netPrice = parseFloat(manualForm.net_price) || 0;
    const ppn = parseFloat(manualForm.ppn) || 0;
    const ppbkb = parseFloat(manualForm.ppbkb) || 0;
    const pph = parseFloat(manualForm.pph) || 0;

    let totalAmount;
    const hasFinancialData = netPrice > 0 || ppn > 0 || ppbkb > 0 || pph > 0;
    
    if (hasFinancialData) {
      totalAmount = netPrice + ppn + ppbkb + pph;
    } else {
      totalAmount = parseFloat(manualForm.total_amount) || 0;
      if (totalAmount <= 0) {
        toast.error('Total Amount wajib diisi jika Net Price, PPn, PPh, dan PPBKB kosong');
        return;
      }
    }

    try {
      const payload = {
        transaction_id: editingTransaction.transaction_id,
        schedule_agreement_number: manualForm.schedule_agreement_number,
        creation_date: new Date(manualForm.creation_date).toISOString(),
        net_price: netPrice || null,
        ppn: ppn || null,
        ppbkb: ppbkb || null,
        pph: pph || null,
        total_amount: totalAmount,
        product_details: productDetails.length > 0 ? productDetails.map(pd => ({
          material: pd.material || null,
          trip_number: parseInt(pd.trip_number),
          quantity: parseInt(pd.quantity),
          unit: pd.unit,
          delivery_date: pd.delivery_date,
          transporter: pd.transporter || null,
          confirmed_quantity: pd.confirmed_quantity ? parseInt(pd.confirmed_quantity) : null,
          confirmed_date: pd.confirmed_date || null
        })) : []
      };

      await axios.put(`${API}/transactions/${editingTransaction.transaction_id}`, payload);
      toast.success('Transaksi berhasil diupdate');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      resetManualForm();
      fetchTransactions();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal mengupdate transaksi';
      toast.error(errorMsg);
      console.error(error);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!manualForm.schedule_agreement_number) {
      toast.error('Schedule Agreement Number wajib diisi');
      return;
    }

    // Calculate total if Net Price, PPn, PPh, PPBKB filled
    const netPrice = parseFloat(manualForm.net_price) || 0;
    const ppn = parseFloat(manualForm.ppn) || 0;
    const ppbkb = parseFloat(manualForm.ppbkb) || 0;
    const pph = parseFloat(manualForm.pph) || 0;

    let totalAmount;
    const hasFinancialData = netPrice > 0 || ppn > 0 || ppbkb > 0 || pph > 0;
    
    if (hasFinancialData) {
      // Auto-calculate
      totalAmount = netPrice + ppn + ppbkb + pph;
    } else {
      // Use manual Total Amount - WAJIB diisi jika semua field kosong
      totalAmount = parseFloat(manualForm.total_amount) || 0;
      if (totalAmount <= 0) {
        toast.error('Total Amount wajib diisi jika Net Price, PPn, PPh, dan PPBKB kosong');
        return;
      }
    }

    try {
      const payload = {
        transaction_id: manualForm.transaction_id || null,
        schedule_agreement_number: manualForm.schedule_agreement_number,
        creation_date: new Date(manualForm.creation_date).toISOString(),
        net_price: netPrice || null,
        ppn: ppn || null,
        ppbkb: ppbkb || null,
        pph: pph || null,
        total_amount: totalAmount,
        product_details: productDetails.length > 0 ? productDetails.map(pd => ({
          material: pd.material || null,
          trip_number: parseInt(pd.trip_number),
          quantity: parseInt(pd.quantity),
          unit: pd.unit,
          delivery_date: pd.delivery_date,
          transporter: pd.transporter || null,  // Nullable
          confirmed_quantity: pd.confirmed_quantity ? parseInt(pd.confirmed_quantity) : null,
          confirmed_date: pd.confirmed_date || null
        })) : []
      };

      await axios.post(`${API}/transactions/manual`, payload);
      toast.success('Transaksi manual berhasil ditambahkan');
      setManualDialogOpen(false);
      resetManualForm();
      setCurrentPage(1); // Reset to page 1
      fetchTransactions();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal menambahkan transaksi';
      toast.error(errorMsg);
      console.error(error);
    }
  };

  const resetManualForm = () => {
    setManualForm({
      transaction_id: '',
      schedule_agreement_number: '',
      creation_date: new Date().toISOString().slice(0, 16),
      net_price: '',
      ppn: '',
      ppbkb: '',
      pph: '',
      total_amount: ''
    });
    setProductDetails([]);
    setShowProductForm(false);
  };
  
  // Product detail handlers
  const addProductDetail = () => {
    setProductDetails([...productDetails, {
      material: '',
      trip_number: 1,
      quantity: '',
      unit: 'KG',
      delivery_date: new Date().toISOString().slice(0, 10),
      transporter: '',  // Nullable
      confirmed_quantity: '',
      confirmed_date: ''
    }]);
  };
  
  const removeProductDetail = (index) => {
    setProductDetails(productDetails.filter((_, i) => i !== index));
  };
  
  const updateProductDetail = (index, field, value) => {
    const updated = [...productDetails];
    updated[index][field] = value;
    setProductDetails(updated);
  };

  // Calculate total otomatis saat Net Price, PPn, atau PPBKB berubah
  const calculateTotal = () => {
    const netPrice = parseFloat(manualForm.net_price) || 0;
    const ppn = parseFloat(manualForm.ppn) || 0;
    const ppbkb = parseFloat(manualForm.ppbkb) || 0;
    const pph = parseFloat(manualForm.pph) || 0;

    if (netPrice > 0 || ppn > 0 || ppbkb > 0) {
      return netPrice + ppn + ppbkb + pph;
    }
    return parseFloat(manualForm.total_amount) || 0;
  };

  return (
    <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 min-h-screen">
      <Card className="shadow-xl border-emerald-200">
        <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-t-lg">
          <CardTitle className="text-3xl font-bold">Laporan Pembelian LPG PSO</CardTitle>
          <CardDescription className="text-emerald-50 text-base">
            Kelola data transaksi pembelian LPG 3 kg Pertamina
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Upload Section - Multi File */}
          <div className="mb-8 p-4 md:p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border-2 border-emerald-200">
            <h3 className="text-base md:text-lg font-semibold mb-4 text-emerald-800 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload File Excel
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="file"
                accept=".xls,.xlsx"
                multiple
                onChange={handleFilesSelect}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button type="button" variant="outline" className="cursor-pointer" asChild>
                  <span>
                    <FileText className="mr-2 h-4 w-4" />
                    Pilih File
                  </span>
                </Button>
              </label>
              
              <Button 
                onClick={handleUpload} 
                disabled={uploading || selectedFiles.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? 'Mengupload...' : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
              </Button>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-emerald-700">
                  File Terpilih ({selectedFiles.length}):
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between bg-white p-3 rounded border border-emerald-200"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-medium">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-sm text-emerald-700 mt-3">
              💡 Anda dapat memilih dan mengupload beberapa file Excel sekaligus
            </p>
          </div>

          {/* Filter Section */}
          <div className="mb-6 p-4 md:p-6 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-base md:text-lg font-semibold mb-4 text-gray-800">Filter Berdasarkan Tanggal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Tanggal Mulai</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'dd/MM/yyyy') : 'Pilih tanggal'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Tanggal Akhir</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'dd/MM/yyyy') : 'Pilih tanggal'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date > new Date() || (startDate && date < startDate)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                <Button onClick={handleFilter} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Filter
                </Button>
                <Button onClick={handleResetFilter} variant="outline" className="flex-1">
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="text-sm text-gray-600">
                Total: <span className="font-semibold text-emerald-700">{totalItems}</span> transaksi
              </div>
              {selectedRows.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-blue-600">
                    {selectedRows.length} terpilih
                  </div>
                  <Button
                    onClick={handleBulkDelete}
                    size="sm"
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Hapus Terpilih
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                    <Plus className="mr-2 h-4 w-4" />
                    Tambah Manual
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Tambah Transaksi Manual</DialogTitle>
                    <DialogDescription>
                      Untuk transaksi yang tidak terlapor di Excel
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleManualSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 md:col-span-1">
                        <Label>Transaction ID (opsional)</Label>
                        <Input
                          value={manualForm.transaction_id}
                          onChange={(e) => setManualForm({...manualForm, transaction_id: e.target.value})}
                          placeholder="Kosongkan untuk auto-generate"
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <Label>Schedule Agreement Number *</Label>
                        <Input
                          value={manualForm.schedule_agreement_number}
                          onChange={(e) => setManualForm({...manualForm, schedule_agreement_number: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Tanggal Dibuat *</Label>
                      <Input
                        type="datetime-local"
                        value={manualForm.creation_date}
                        onChange={(e) => setManualForm({...manualForm, creation_date: e.target.value})}
                        required
                      />
                    </div>

                    <div className="p-4 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm font-medium text-blue-900 mb-2">💡 Ketentuan Pengisian:</p>
                      <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>Jika Net Price, PPn, PPBKB diisi → Total Amount otomatis dihitung</li>
                        <li>Jika Net Price, PPn, PPBKB kosong → Total Amount wajib diisi manual</li>
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Net Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={manualForm.net_price}
                          onChange={(e) => setManualForm({...manualForm, net_price: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPn</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={manualForm.ppn}
                          onChange={(e) => setManualForm({...manualForm, ppn: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPBKB</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={manualForm.ppbkb}
                          onChange={(e) => setManualForm({...manualForm, ppbkb: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPh</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={manualForm.pph}
                          onChange={(e) => setManualForm({...manualForm, pph: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>
                        Total Amount {calculateTotal() > 0 && '(Otomatis)'}
                        {calculateTotal() === 0 && <span className="text-red-600 ml-1">*</span>}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={calculateTotal() > 0 ? calculateTotal() : manualForm.total_amount}
                        onChange={(e) => setManualForm({...manualForm, total_amount: e.target.value})}
                        disabled={calculateTotal() > 0}
                        placeholder="Wajib diisi jika field di atas kosong"
                        className={calculateTotal() > 0 ? 'bg-gray-100 font-semibold text-emerald-700' : ''}
                        required={calculateTotal() === 0}
                      />
                      {calculateTotal() === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          💡 Total Amount wajib diisi karena Net Price, PPn, PPh, dan PPBKB kosong
                        </p>
                      )}
                      {calculateTotal() > 0 && (
                        <p className="text-xs text-emerald-600 mt-1">
                          ✓ Total Amount dihitung otomatis: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(calculateTotal())}
                        </p>
                      )}
                    </div>

                    {/* Product Details Section */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base font-semibold">Detail Produk (Opsional)</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowProductForm(!showProductForm)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          {showProductForm ? 'Sembunyikan' : 'Tampilkan'} Form Produk
                        </Button>
                      </div>
                      
                      {showProductForm && (
                        <div className="space-y-4">
                          {productDetails.map((detail, index) => (
                            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-semibold text-gray-700">Produk #{index + 1}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeProductDetail(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                  <Label className="text-sm">Material/Produk</Label>
                                  <Input
                                    value={detail.material}
                                    onChange={(e) => updateProductDetail(index, 'material', e.target.value)}
                                    placeholder="Nama material"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Jumlah LO *</Label>
                                  <Input
                                    type="number"
                                    value={detail.trip_number}
                                    onChange={(e) => updateProductDetail(index, 'trip_number', e.target.value)}
                                    required
                                    min="1"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Kuantitas per LO *</Label>
                                  <Input
                                    type="number"
                                    value={detail.quantity}
                                    onChange={(e) => updateProductDetail(index, 'quantity', e.target.value)}
                                    required
                                    min="1"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Satuan *</Label>
                                  <Select 
                                    value={detail.unit} 
                                    onValueChange={(value) => updateProductDetail(index, 'unit', value)}
                                  >
                                    <SelectTrigger className="text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="KG">KG</SelectItem>
                                      <SelectItem value="TON">TON</SelectItem>
                                      <SelectItem value="LITER">LITER</SelectItem>
                                      <SelectItem value="UNIT">UNIT</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Tanggal Pengiriman *</Label>
                                  <Input
                                    type="date"
                                    value={detail.delivery_date}
                                    onChange={(e) => updateProductDetail(index, 'delivery_date', e.target.value)}
                                    required
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div className="col-span-2">
                                  <Label className="text-sm">Transportir (Opsional)</Label>
                                  <Input
                                    value={detail.transporter}
                                    onChange={(e) => updateProductDetail(index, 'transporter', e.target.value)}
                                    placeholder="Nama transportir"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Kuantitas Terkonfirmasi</Label>
                                  <Input
                                    type="number"
                                    value={detail.confirmed_quantity}
                                    onChange={(e) => updateProductDetail(index, 'confirmed_quantity', e.target.value)}
                                    min="0"
                                    placeholder="Opsional"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Tanggal Terkonfirmasi</Label>
                                  <Input
                                    type="date"
                                    value={detail.confirmed_date}
                                    onChange={(e) => updateProductDetail(index, 'confirmed_date', e.target.value)}
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addProductDetail}
                            className="w-full border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Tambah Produk
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        Simpan
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setManualDialogOpen(false);
                          resetManualForm();
                        }}
                      >
                        Batal
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Edit Transaction Dialog */}
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-amber-800">
                      Edit Transaksi - {editingTransaction?.transaction_id}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleEditSubmit} className="space-y-4">
                    {/* Transaction ID - Read Only */}
                    <div>
                      <Label>Transaction ID</Label>
                      <Input
                        value={manualForm.transaction_id}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>

                    <div>
                      <Label>Schedule Agreement Number *</Label>
                      <Input
                        value={manualForm.schedule_agreement_number}
                        onChange={(e) => setManualForm({...manualForm, schedule_agreement_number: e.target.value})}
                        required
                      />
                    </div>

                    <div>
                      <Label>Creation Date</Label>
                      <Input
                        type="datetime-local"
                        value={manualForm.creation_date}
                        onChange={(e) => setManualForm({...manualForm, creation_date: e.target.value})}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Net Price</Label>
                        <Input
                          type="number"
                          value={manualForm.net_price}
                          onChange={(e) => setManualForm({...manualForm, net_price: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPn</Label>
                        <Input
                          type="number"
                          value={manualForm.ppn}
                          onChange={(e) => setManualForm({...manualForm, ppn: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPh</Label>
                        <Input
                          type="number"
                          value={manualForm.pph}
                          onChange={(e) => setManualForm({...manualForm, pph: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>PPBKB</Label>
                        <Input
                          type="number"
                          value={manualForm.ppbkb}
                          onChange={(e) => setManualForm({...manualForm, ppbkb: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Total Amount *</Label>
                      <Input
                        type="number"
                        value={manualForm.total_amount}
                        onChange={(e) => setManualForm({...manualForm, total_amount: e.target.value})}
                        placeholder="Wajib diisi atau akan di-calculate otomatis"
                      />
                      {calculateTotal() > 0 && (
                        <p className="text-xs text-emerald-600 mt-1">
                          ✓ Total Amount dihitung otomatis: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(calculateTotal())}
                        </p>
                      )}
                    </div>

                    {/* Product Details Section */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base font-semibold">Detail Produk</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowProductForm(!showProductForm)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          {showProductForm ? 'Sembunyikan' : 'Tampilkan'} Form Produk
                        </Button>
                      </div>
                      
                      {showProductForm && (
                        <div className="space-y-4">
                          {productDetails.map((detail, index) => (
                            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-semibold text-gray-700">Produk #{index + 1}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeProductDetail(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                  <Label className="text-sm">Material/Produk</Label>
                                  <Input
                                    value={detail.material}
                                    onChange={(e) => updateProductDetail(index, 'material', e.target.value)}
                                    placeholder="Nama material"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Jumlah LO *</Label>
                                  <Input
                                    type="number"
                                    value={detail.trip_number}
                                    onChange={(e) => updateProductDetail(index, 'trip_number', e.target.value)}
                                    required
                                    min="1"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Kuantitas per LO *</Label>
                                  <Input
                                    type="number"
                                    value={detail.quantity}
                                    onChange={(e) => updateProductDetail(index, 'quantity', e.target.value)}
                                    required
                                    min="1"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Satuan *</Label>
                                  <Select 
                                    value={detail.unit} 
                                    onValueChange={(value) => updateProductDetail(index, 'unit', value)}
                                  >
                                    <SelectTrigger className="text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="KG">KG</SelectItem>
                                      <SelectItem value="TON">TON</SelectItem>
                                      <SelectItem value="LITER">LITER</SelectItem>
                                      <SelectItem value="UNIT">UNIT</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Tanggal Pengiriman *</Label>
                                  <Input
                                    type="date"
                                    value={detail.delivery_date}
                                    onChange={(e) => updateProductDetail(index, 'delivery_date', e.target.value)}
                                    required
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div className="col-span-2">
                                  <Label className="text-sm">Transportir (Opsional)</Label>
                                  <Input
                                    value={detail.transporter}
                                    onChange={(e) => updateProductDetail(index, 'transporter', e.target.value)}
                                    placeholder="Nama transportir"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Kuantitas Terkonfirmasi</Label>
                                  <Input
                                    type="number"
                                    value={detail.confirmed_quantity}
                                    onChange={(e) => updateProductDetail(index, 'confirmed_quantity', e.target.value)}
                                    min="0"
                                    placeholder="Opsional"
                                    className="text-sm"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm">Tanggal Terkonfirmasi</Label>
                                  <Input
                                    type="date"
                                    value={detail.confirmed_date}
                                    onChange={(e) => updateProductDetail(index, 'confirmed_date', e.target.value)}
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addProductDetail}
                            className="w-full border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Tambah Produk
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                        Update
                      </Button>
                      <Button 
                        type="button"
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setEditDialogOpen(false);
                          setEditingTransaction(null);
                          resetManualForm();
                        }}
                      >
                        Batal
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Button 
                onClick={handleDownload} 
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Excel
              </Button>
              {currentUser?.role === 'Admin' && (
                <Button 
                  onClick={handleDeleteAll} 
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Hapus Semua
                </Button>
              )}
            </div>
          </div>

          {/* Rows per page selector - Above table */}
          {!loading && transactions.length > 0 && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Baris per halaman:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-sm text-gray-600">
                Total: {totalItems} data
              </div>
            </div>
          )}

          {/* Table - Desktop */}
          <div className="hidden md:block rounded-md border border-emerald-200 overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-emerald-600">
                <TableRow>
                  <TableHead className="text-white font-semibold w-12 text-center">
                    <Checkbox
                      checked={selectAll || (transactions.length > 0 && selectedRows.length === transactions.length)}
                      onCheckedChange={handleSelectAll}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-emerald-600"
                    />
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('transaction_id')}
                  >
                    <div className="flex items-center gap-1">
                      Transaction ID
                      {sortBy === 'transaction_id' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('schedule_agreement_number')}
                  >
                    <div className="flex items-center gap-1">
                      Schedule Agreement Number
                      {sortBy === 'schedule_agreement_number' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('creation_date')}
                  >
                    <div className="flex items-center gap-1">
                      Tanggal Dibuat
                      {sortBy === 'creation_date' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold text-right cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('net_price')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Net Price
                      {sortBy === 'net_price' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold text-right cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('ppn')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      PPn
                      {sortBy === 'ppn' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold text-right cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('pph')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      PPh
                      {sortBy === 'pph' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold text-right cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('ppbkb')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      PPBKB
                      {sortBy === 'ppbkb' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold text-right cursor-pointer hover:bg-emerald-700"
                    onClick={() => handleSort('total_amount')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Total Amount
                      {sortBy === 'total_amount' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                      Memuat data...
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                      Belum ada data transaksi
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((trans, index) => (
                    <TableRow key={trans.id} className={index % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'}>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={selectedRows.includes(trans.transaction_id)}
                          onCheckedChange={() => handleSelectRow(trans.transaction_id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{trans.transaction_id}</TableCell>
                      <TableCell>{trans.schedule_agreement_number}</TableCell>
                      <TableCell>{formatDateIndonesia(trans.creation_date)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trans.net_price)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trans.ppn)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trans.pph)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trans.ppbkb)}</TableCell>
                      <TableCell className="text-right font-semibold font-mono text-emerald-700">
                        {formatCurrency(trans.total_amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewProducts(trans.transaction_id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Lihat Produk"
                          >
                            <Package className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(trans)}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(trans.transaction_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Memuat data...</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Belum ada data transaksi</div>
            ) : (
              transactions.map((trans, index) => (
                <Card key={trans.id} className={`overflow-hidden ${selectedRows.includes(trans.transaction_id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <CardContent className="p-4 space-y-3">
                    {/* Transaction ID & Actions */}
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Checkbox
                          checked={selectedRows.includes(trans.transaction_id)}
                          onCheckedChange={() => handleSelectRow(trans.transaction_id)}
                          className="mt-1 flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-gray-500 mb-1">Transaction ID</div>
                          <div className="font-semibold text-sm truncate">{trans.transaction_id}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewProducts(trans.transaction_id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                          title="Lihat Produk"
                        >
                          <Package className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(trans)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(trans.transaction_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          title="Hapus"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Schedule Agreement */}
                    <div>
                      <div className="text-xs text-gray-500">Schedule Agreement Number</div>
                      <div className="text-sm">{trans.schedule_agreement_number}</div>
                    </div>
                    
                    {/* Tanggal */}
                    <div>
                      <div className="text-xs text-gray-500">Tanggal Dibuat</div>
                      <div className="text-sm">{formatDateIndonesia(trans.creation_date)}</div>
                    </div>
                    
                    {/* Financial Data - Single Column */}
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">Net Price</div>
                        <div className="text-sm font-mono font-medium">{formatCurrency(trans.net_price)}</div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">PPn</div>
                        <div className="text-sm font-mono font-medium">{formatCurrency(trans.ppn)}</div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">PPh</div>
                        <div className="text-sm font-mono font-medium">{formatCurrency(trans.pph)}</div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">PPBKB</div>
                        <div className="text-sm font-mono font-medium">{formatCurrency(trans.ppbkb)}</div>
                      </div>
                    </div>
                    
                    {/* Total */}
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-500">Total Amount</div>
                      <div className="text-base font-semibold font-mono text-emerald-700">
                        {formatCurrency(trans.total_amount)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination Controls - Bottom */}
          {!loading && transactions.length > 0 && totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              {/* Page info */}
              <div className="text-sm text-gray-600">
                Menampilkan {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} dari {totalItems} data
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="hidden sm:flex"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-9 ${currentPage === pageNum ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="hidden sm:flex"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Details Modal */}
      <Dialog open={productModalOpen} onOpenChange={setProductModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-emerald-800">
              Daftar Produk - {selectedTransactionId}
            </DialogTitle>
          </DialogHeader>
          
          {loadingProducts ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Memuat data produk...</div>
            </div>
          ) : selectedProducts.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Tidak ada data produk</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-emerald-600 text-white">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Produk</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm">Jumlah LO</th>
                      <th className="border border-gray-300 px-3 py-2 text-right text-sm">Kuantitas per LO</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm">Satuan</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm">Tanggal Pengiriman</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm">Transportir</th>
                      <th className="border border-gray-300 px-3 py-2 text-right text-sm">Kuantitas Terkonfirmasi</th>
                      <th className="border border-gray-300 px-3 py-2 text-center text-sm">Tanggal Terkonfirmasi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts.map((product, idx) => (
                      <tr key={product.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-3 py-2 text-sm">{product.material}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-sm">{product.trip_number}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-sm">{product.quantity}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-sm">{product.unit}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-sm">{product.delivery_date || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-sm">{product.transporter || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-sm">{product.confirmed_quantity || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-sm">{product.confirmed_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {selectedProducts.map((product, idx) => (
                  <Card key={product.id} className="border-2 border-gray-200">
                    <CardContent className="p-4 space-y-2">
                      <div className="font-semibold text-sm text-emerald-700 mb-2">
                        LO #{idx+1}
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Produk:</span>
                          <span className="font-medium text-right">{product.material}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Kuantitas per LO:</span>
                          <span className="font-medium">{product.quantity} {product.unit}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tanggal Pengiriman:</span>
                          <span className="font-medium">{product.delivery_date || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transportir:</span>
                          <span className="font-medium">{product.transporter || '-'}</span>
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Kuantitas Terkonfirmasi:</span>
                            <span className="font-medium">{product.confirmed_quantity || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tanggal Terkonfirmasi:</span>
                            <span className="font-medium">{product.confirmed_date || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-sm text-gray-500 text-center pt-4 border-t">
                Total: {selectedProducts.length} produk
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Penghapusan</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus transaksi ini? Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Penghapusan Massal</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus <span className="font-bold text-red-600">{selectedRows.length} transaksi</span> yang dipilih? 
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Hapus {selectedRows.length} Transaksi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={deleteAllConfirmOpen} onOpenChange={setDeleteAllConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ PERINGATAN: Hapus Semua Data</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="font-semibold">
                Tindakan ini akan menghapus SEMUA transaksi yang ada di database!
              </p>
              <p>
                Anda akan kehilangan <span className="font-bold text-red-600">{totalItems} transaksi</span> secara permanen. 
                Tindakan ini <span className="font-bold">TIDAK DAPAT DIBATALKAN</span>.
              </p>
              <p className="text-amber-600">
                Apakah Anda benar-benar yakin?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAll}
              className="bg-red-600 hover:bg-red-700"
            >
              Ya, Hapus Semua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LPGReport;
